import { formatCurrency, getLenis } from '@theme/utilities';
import { CartErrorEvent } from '@theme/events';

// CR-5B bridges chooser state to Kaching; Pebble retains its native cart request.
// CR-9F: nothing is chosen for her at first (F2, F4); the route switch is soft (F3).
// CR-9G: how long the chooser waits for Kaching's widget, counting only time the page is visible.
const KACHING_WAIT_MS = 60000;

class LumeiaBundleChooser extends HTMLElement {
  connectedCallback() {
    if (this.started) return;
    this.started = true;
    this.bridgeFailed = false;
    this.abort = new AbortController();
    this.config = JSON.parse(this.querySelector('[data-chooser-config]').textContent);
    this.ui = this.querySelector('[data-chooser-ui]');
    // Box option blocks, in block order; each names its Kaching quantity.
    this.tiers = [...this.querySelectorAll('script[data-chooser-tier]')].map((script) => {
      try { const tier = JSON.parse(script.textContent); return { ...tier, quantity: Number(tier.quantity) }; } catch { return null; }
    }).filter((tier) => tier && [1, 2, 3].includes(tier.quantity));
    this.path = 'new';
    // F4: the box option chosen at first, from the editor; 0 is none.
    this.startQuantity = [1, 2, 3].includes(Number(this.config.start_quantity)) ? Number(this.config.start_quantity) : 0;
    this.quantity = this.startQuantity;
    this.sock = null;
    this.picked = false;
    // CR-9F F2: no flavour is chosen for her; only a back or forward arrival brings her own picks back.
    this.recent = [];
    try {
      if (performance.getEntriesByType('navigation')[0]?.type === 'back_forward') this.saved = this.readState();
      else sessionStorage.removeItem(this.stateKey());
    } catch { /* Storage that throws is ignored. */ }
    this.startCap();
    this.connectBridge();
    this.connectSticky();
    this.ui.addEventListener('change', (event) => this.change(event), { signal: this.abort.signal });
    this.ui.addEventListener('click', (event) => this.click(event), { signal: this.abort.signal });
    if (this.config.look?.box_title?.class) window.addEventListener('resize', () => this.alignRadio(), { signal: this.abort.signal });
    // A page kept in the back-forward cache: her saved choice comes back, or the fresh start after an add.
    // G2: a tap or change in the chooser ends a press that waits for Kaching; she presses again.
    for (const name of ['click', 'change']) this.ui.addEventListener(name, () => this.endWait(), { capture: true, signal: this.abort.signal });
    // CR-9J: her first press on a chooser control turns scroll anchoring off for the page (lumeia-custom.css), before that press renders.
    for (const name of ['click', 'change']) this.ui.addEventListener(name, (event) => {
      if (event.target.closest('[data-path], [data-tier], [data-sock], [data-variant], [data-mixed], [data-deal-bar-id], label')) this.setAttribute('data-picked', '');
    }, { capture: true, signal: this.abort.signal });
    window.addEventListener('pageshow', (event) => {
      // The first load's own pageshow comes after the load event, which a slow page can reach mid-wait.
      if (!event.persisted) return;
      this.endWait();
      if (!this.bars || this.bridgeFailed) return;
      this.releasePress();
      const state = this.readState();
      if (state) this.applyState(state); else this.freshStart();
      this.reconcile(this.quantity);
      this.render();
    }, { signal: this.abort.signal });
    // App blocks can insert their JSON after this block has connected.
    if (this.readDeal()) this.dealRead();
    else {
      if (this.dataset.designMode === 'true' && this.availableVariants().length) { this.bars = []; this.render(); this.hidden = false; }
      // G3: the deal is looked for until the same cap.
      this.observer = new MutationObserver(() => { if (this.readDeal()) this.dealRead(); });
      this.observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  dealRead() {
    this.dealDone = true;
    this.observer?.disconnect();
    this.observer = null;
    if (this.widget) this.endCap();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    this.observer = null;
    clearTimeout(this.toastTimer);
    clearTimeout(this.capTimer);
    this.capTimer = null;
    this.widgetObserver?.disconnect();
    this.restoreSticky();
    this.releasePress();
    if (this.waiting) this.form?.refs.addToCartButtonContainer?.removeLoading();
    this.widget = null;
    this.waiting = false;
    this.waitToken = null;
    this.syncing = false;
    this.started = false;
  }

  // As Kaching does: the deal named by this section's placeholder `manual-deal-block-id`, else the first script's.
  pickDeal() {
    const deals = [...document.querySelectorAll(`script.kaching-bundles-deal-block-settings[data-product-id="${this.dataset.productId}"]`)]
      .map((script) => { try { return JSON.parse(script.textContent); } catch { return null; } })
      .filter((deal) => Array.isArray(deal?.dealBars));
    if (!deals.length) return null;
    const placeholder = [...(this.section || document).querySelectorAll('kaching-bundle[manual-deal-block-id]')]
      .find((item) => [null, this.dataset.productId].includes(item.getAttribute('product-id')));
    const manual = placeholder?.getAttribute('manual-deal-block-id').trim();
    const match = manual ? deals.find((deal) => deal.id === manual || deal.nanoId === manual) : null;
    return { deal: match || deals[0], source: match ? 'manual' : 'first' };
  }

  readDeal() {
    if (this.bridgeFailed) return true;
    const picked = this.pickDeal();
    if (!picked) return false;
    try {
      this.dataset.dealId = picked.deal.nanoId || picked.deal.id || '';
      this.dataset.dealSource = picked.source;
      // CR-9K: the chooser's prices and gift lines need per-option gifts; a deal with progressive gifts sells through Kaching's own widget.
      if (picked.deal.progressiveGiftsEnabled === true && picked.deal.progressiveGifts?.gifts?.length) { this.restoreWidget(); return true; }
      this.allBars = picked.deal.dealBars.filter((bar) => bar.id && [1, 2, 3].includes(bar.quantity) && Array.isArray(bar.freeGifts)
        && ['percentage', 'default'].includes(bar.discountType) && Number.isFinite(Number(bar.discountValue)));
      // Hidden means gone: a Kaching option with no Box option block is never offered or picked.
      this.bars = this.allBars.filter((bar) => this.tier(bar.quantity));
      // In the theme editor the chooser stays up with no box options, so its notice shows.
      if ((!this.bars.length && this.dataset.designMode !== 'true') || !this.availableVariants().length) { this.hidden = true; return true; }
      if (this.saved) this.applyState(this.saved);
      this.saved = null;
      if (!this.pathBars().length && this.pathBars('returning').length) this.path = 'returning';
      this.reconcile(this.quantity);
      this.render();
      this.hidden = false;
      return true;
    } catch { return false; }
  }

  // Route A: keep Kaching's retained widget current; Pebble owns the request and cart events.
  connectBridge() {
    this.section = this.closest('.shopify-section');
    this.form = [...(this.section?.querySelectorAll('product-form-component') || [])]
      .find((form) => form.dataset.productId === this.dataset.productId);
    if (!this.form?.querySelector('button[type="submit"]')) { this.restoreWidget(); return; }
    const signal = this.abort.signal;
    // Also called at the cap and while a press waits: an element that upgrades in place adds no node.
    const ready = this.widgetReady = () => {
      if (signal.aborted || this.bridgeFailed) return;
      if (this.widget) { this.queueSync(); return; }
      const widget = this.section?.querySelector('kaching-bundles-block');
      if (typeof widget?.items !== 'function') return;
      this.widget = widget;
      this.widgetTakenAt = performance.now();
      this.widgetObserver?.disconnect();
      this.endCap();
      widget.addEventListener('items-changed', () => {
        this.itemsChanged = true;
        this.synced = this.matchesWidget();
      }, { signal });
      this.queueSync();
    };
    document.addEventListener('kaching-bundles-block-loaded', ready, { capture: true, signal });
    this.widgetObserver = new MutationObserver(ready);
    this.widgetObserver.observe(this.section || document.body, { childList: true, subtree: true });
    ready();
    window.addEventListener('click', (event) => {
      if (!this.form?.isConnected) { this.restoreWidget(); return; }
      const button = event.target.closest('button[type="submit"]');
      if (button && button.closest('product-form-component') === this.form) this.guardPress(event);
    }, { capture: true, signal });
    window.addEventListener('submit', (event) => {
      if (!this.form?.isConnected || event.target.closest('product-form-component') !== this.form) return;
      if (this.allowSubmit) { this.allowSubmit = false; return; }
      this.guardPress(event);
    }, { capture: true, signal });
    this.form?.addEventListener('cart:update', (event) => this.correctCartCount(event), { capture: true, signal });
    for (const name of ['cart:update', 'cart:error']) {
      this.form?.addEventListener(name, (event) => {
        if (event.target !== this.form) return;
        this.releasePress();
        if (name === 'cart:update') try { sessionStorage.removeItem(this.stateKey()); } catch { /* ignored */ }
      }, { signal });
    }
  }

  restoreWidget() {
    try { window.clarity?.('event', 'chooser_stand_down'); } catch { /* ignored */ }
    this.bridgeFailed = true;
    this.synced = false;
    this.hidden = true;
    this.restoreSticky();
    this.removeAttribute('data-kaching-active');
    this.widgetObserver?.disconnect();
    this.form?.refs.addToCartButtonContainer?.removeLoading();
  }

  // G1: no widget yet is not a failure. One cap timer; it stops while the page is hidden and keeps what is left.
  startCap() {
    this.capLeft = KACHING_WAIT_MS;
    this.capEnded = false;
    this.dealDone = false;
    this.runCap();
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pauseCap(); else this.runCap();
    }, { signal: this.abort.signal });
  }

  runCap() {
    if (this.capEnded || this.capTimer || document.hidden) return;
    this.capStart = performance.now();
    this.capTimer = setTimeout(() => this.capReached(), this.capLeft);
  }

  pauseCap() {
    if (!this.capTimer) return;
    clearTimeout(this.capTimer);
    this.capTimer = null;
    this.capLeft = Math.max(0, this.capLeft - (performance.now() - this.capStart));
  }

  endCap() {
    // While the deal is still unread, the cap keeps running for readDeal's observer.
    if (!this.dealDone) return;
    clearTimeout(this.capTimer);
    this.capTimer = null;
    this.capEnded = true;
  }

  capReached() {
    if (!this.widget) this.widgetReady?.();
    this.capTimer = null;
    this.capEnded = true;
    this.observer?.disconnect();
    this.observer = null;
    if (!this.widget && !this.bridgeFailed) this.restoreWidget();
  }

  connectSticky() {
    this.sticky = this.section?.querySelector('sticky-add-to-cart');
    if (!this.sticky) return;
    this.saveSticky();
    const signal = this.abort.signal;
    this.section.addEventListener('variant:update', (event) => {
      if (String(event.detail?.data?.productId) !== this.dataset.productId) return;
      queueMicrotask(() => {
        if (signal.aborted) return;
        if (event.detail?.data?.html?.querySelector('sticky-add-to-cart')) this.saveSticky();
        this.updateSticky();
      });
    }, { signal });
    this.ui.addEventListener('click', (event) => {
      const input = event.target.closest('[data-path], [data-tier], [data-sock], [data-variant], [data-mixed]')
        || event.target.closest('label')?.querySelector('[data-path], [data-tier], [data-sock]');
      const card = event.target.closest('[data-deal-bar-id]');
      if ((input && !input.disabled) || (card && !card.contains(event.target.closest('details, fieldset, button')))) {
        this.picked = true;
        queueMicrotask(() => { if (!signal.aborted) this.updateSticky(); });
      }
    }, { capture: true, signal });
    window.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button || button !== this.sticky.querySelector('[ref="addToCartButton"]')
        || !this.sticky.hasAttribute('data-chooser-sticky')) return;
      if (this.inFlight || this.waiting) { this.stopPress(event); return; }
      if (this.stickyReady()) { this.stickyPress = true; return; }
      this.stopPress(event);
      // CR-9N (owner, 28 Oct: "takes me to 'build your habit' rather than First time or coming back?"): with nothing
      // pressed yet, the press leads to the first step; once she has pressed a control it leads to the boxes as before.
      if (!this.quantity && !this.picked) {
        const path = this.ui.querySelector('[data-path]:checked');
        this.scrollChoice(path?.closest('fieldset') || this, path, !this.moves());
        return;
      }
      if (!this.quantity) { this.scrollToBoxes(); return; }
      if (this.picked && this.gifts(this.bar()).some((gift) => gift.key === 'socks') && !this.sock) {
        this.sockError = true;
        this.blurChoice();
        this.render();
        this.leadTo('sock');
      } else if (this.picked && this.recent.length < this.quantity) {
        this.flavourError = true;
        this.blurChoice();
        this.render();
        this.leadTo('flavour');
      } else this.scrollChoice(this, this.ui.querySelector('[data-tier]:checked'));
    }, { capture: true, signal });
  }

  saveSticky() {
    const price = this.sticky.querySelector('product-price');
    const variant = this.sticky.querySelector('.sticky-add-to-cart__variant');
    this.stickyDefault = { text: this.sticky.querySelector('[ref="addToCartText"]')?.textContent,
      accessible: this.sticky.querySelector('[ref="addToCartButton"]')?.getAttribute('aria-label'), price: price?.innerHTML, priceStyle: price?.getAttribute('style'),
      variant: variant?.innerHTML, variantStyle: variant?.getAttribute('style'), variantDir: variant?.getAttribute('dir') };
  }

  choiceComplete() {
    return this.quantity > 0 && this.recent.length === this.quantity
      && (!this.gifts(this.bar()).some((gift) => gift.key === 'socks') || Boolean(this.sock));
  }

  stickyReady() { return this.picked && this.choiceComplete(); }

  updateSticky() {
    if (!this.sticky || this.bridgeFailed || !this.bars?.length) return;
    const tier = this.bar();
    const percent = this.percent(tier);
    const full = (this.availableVariants()[0]?.price || 0) * this.quantity;
    this.sticky.setAttribute('data-chooser-sticky', '');
    const price = this.sticky.querySelector('product-price');
    // F4: with no box chosen there is no price or box line to show.
    for (const element of [price, this.sticky.querySelector('.sticky-add-to-cart__variant')]) if (element && !this.quantity) element.style.display = 'none';
    if (price && this.quantity) { price.style.removeProperty('display'); price.innerHTML = `${this.price(full * (1 - percent / 100))}${percent ? ` <s>${this.price(full)}</s>` : ''}`; }
    const variant = this.sticky.querySelector('.sticky-add-to-cart__variant');
    if (variant && this.quantity) {
      variant.style.removeProperty('display');
      variant.setAttribute('dir', 'auto');
      variant.innerHTML = this.word(this.quantity === 1 ? 'summary_one' : percent ? 'summary_many' : 'summary_no_saving', { n: this.quantity, percent });
    }
    const button = this.sticky.querySelector('[ref="addToCartButton"]');
    const text = this.sticky.querySelector('[ref="addToCartText"]');
    const words = this.stickyReady() ? button?.dataset.addToCartText : this.config.words.choose_bundle;
    if (text) text.textContent = words;
    button?.setAttribute('aria-label', words || '');
  }

  finishStickyPress(notify = false) {
    if (!this.stickyPress) return;
    this.stickyPress = false;
    if (notify) document.dispatchEvent(new CartErrorEvent(this.id, ''));
    else {
      this.sticky?.querySelector('[ref="addToCartButton"]')?.classList.remove('btn--loading');
      this.sticky?.querySelector('[ref="addToCartSpinner"]')?.classList.add('hidden');
    }
  }

  restoreSticky() {
    if (!this.sticky?.hasAttribute('data-chooser-sticky')) return;
    this.finishStickyPress(true);
    this.sticky.removeAttribute('data-chooser-sticky');
    const button = this.sticky.querySelector('[ref="addToCartButton"]');
    const text = this.sticky.querySelector('[ref="addToCartText"]');
    if (text) text.textContent = this.stickyDefault?.text || button?.dataset.addToCartText;
    if (this.stickyDefault?.accessible == null) button?.removeAttribute('aria-label');
    else button?.setAttribute('aria-label', this.stickyDefault.accessible);
    button?.classList.remove('btn--loading');
    this.sticky.querySelector('[ref="addToCartSpinner"]')?.classList.add('hidden');
    for (const [selector, key] of [['product-price', 'price'], ['.sticky-add-to-cart__variant', 'variant']]) {
      const element = this.sticky.querySelector(selector);
      if (!element || !this.stickyDefault) continue;
      element.innerHTML = this.stickyDefault[key] || '';
      const style = this.stickyDefault[`${key}Style`];
      if (style == null) element.removeAttribute('style'); else element.setAttribute('style', style);
      if (key === 'variant') {
        const dir = this.stickyDefault.variantDir;
        if (dir == null) element.removeAttribute('dir'); else element.setAttribute('dir', dir);
      }
    }
  }

  scrollToBoxes() {
    const option = this.ui.querySelector('[data-tier]:not(:disabled)');
    this.scrollChoice(option?.closest('fieldset') || this, option);
  }

  // Resolves once the scroll has settled (F16), at once when none is needed or it is instant.
  scrollChoice(target, focus, instant = false) {
    focus?.focus({ preventScroll: true });
    const raw = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height'));
    const clearance = (Number.isFinite(raw) && raw > 0 ? raw : document.querySelector('header[is="sticky-header"]')?.getBoundingClientRect().height || 0) + 50;
    const top = Math.min(Math.max(0, target.getBoundingClientRect().top + scrollY - clearance), document.documentElement.scrollHeight - innerHeight);
    if (Math.abs(top - scrollY) <= 2) return Promise.resolve();
    const lenis = getLenis();
    return new Promise((resolve) => {
      const done = () => { clearTimeout(timer); removeEventListener('scrollend', done); resolve(); };
      const timer = setTimeout(done, lenis ? 1500 : 600);
      if (lenis) lenis.scrollTo(target, { offset: -clearance, immediate: instant, onComplete: done });
      else {
        if (!instant) addEventListener('scrollend', done, { once: true });
        window.scrollTo({ top, behavior: instant ? 'instant' : 'smooth' });
      }
      if (instant) done();
    });
  }

  // F16: a render with a chooser button focused lets Chrome's scroll anchoring jump the page (about 1000 px
  // on a repeat press), so focus leaves first; leadTo puts it back.
  blurChoice() { if (this.ui.contains(document.activeElement)) document.activeElement.blur(); }

  // F16: a blocked press leads her to the missing sock size or flavour, then flashes its red line, on every press.
  leadTo(kind) {
    const sock = kind === 'sock';
    const target = sock ? this.ui.querySelector('.lumeia-bundle-chooser__socks') : this.ui.querySelector('.lumeia-bundle-chooser__flavours').closest('fieldset');
    if (!target) return;
    const focus = sock ? target.querySelector('[data-sock]') : target.querySelector('[data-variant][data-delta="1"]:not(:disabled)');
    const run = (this.pulseRun = (this.pulseRun || 0) + 1);
    this.scrollChoice(target, focus, !this.moves()).then(() => {
      if (run === this.pulseRun) this.pulse(this.ui.querySelector(sock ? '[data-sock-error]:not([hidden])' : '.lumeia-bundle-chooser__progress[data-error] > .lumeia-bundle-chooser__line'));
    });
  }

  // Bold by a text shadow, so the line does not reflow; the tint flashes twice and fades (about 700 ms).
  // Reduced motion or motion off: bold for 2 s, no flash.
  pulse(line) {
    this.pulseAnimation?.cancel();
    clearTimeout(this.pulseTimer);
    this.pulseLine?.style.removeProperty('text-shadow');
    this.pulseLine = line;
    if (!line) return;
    // A text shadow each side thickens the letters (a text stroke does not animate in Chrome).
    const bold = '0.4px 0 0 currentColor, -0.4px 0 0 currentColor';
    if (!this.moves()) {
      line.style.setProperty('text-shadow', bold);
      this.pulseTimer = setTimeout(() => line.style.removeProperty('text-shadow'), 2000);
      return;
    }
    // The tint is the theme's error colour; the sock notice's text keeps the body colour.
    const error = getComputedStyle(line).getPropertyValue('--color-notification-error').trim();
    const tint = (alpha) => ({ backgroundColor: `rgb(${error} / ${alpha})`, boxShadow: `0 0 0 4px rgb(${error} / ${alpha})` });
    this.pulseAnimation = line.animate([
      { ...tint(0.16), textShadow: bold, borderRadius: '4px', easing: 'ease-out' },
      { ...tint(0.03), textShadow: bold, borderRadius: '4px', offset: 0.3, easing: 'ease-out' },
      { ...tint(0.16), textShadow: bold, borderRadius: '4px', offset: 0.55, easing: 'ease-out' },
      { ...tint(0), textShadow: '0 0 0 transparent, 0 0 0 transparent', borderRadius: '4px' },
    ], { duration: 700 });
  }

  nextFrame() { return new Promise((resolve) => requestAnimationFrame(resolve)); }

  queueSync() {
    this.syncVersion = (this.syncVersion || 0) + 1;
    this.synced = false;
    if (!this.widget || this.syncing || this.bridgeFailed) return;
    this.syncing = true;
    const signal = this.abort.signal;
    this.syncWidget(signal).catch(() => { if (!signal.aborted) this.synced = false; })
      .finally(() => { if (!signal.aborted) this.syncing = false; });
  }

  async syncWidget(signal) {
    let version;
    do {
      if (signal.aborted) return;
      version = this.syncVersion;
      const tier = this.bar();
      if (!tier || this.recent.length !== this.quantity) return;
      const product = JSON.parse(this.widget.getAttribute('product'));
      const labels = this.recent.map((id) => product.variants.find((variant) => Number(variant.id) === id)?.options[0]);
      if (labels.some((label) => !label)) return;
      if (this.widget.selectedDealBarId !== tier.id) this.widget.selectedDealBarId = tier.id;
      await this.nextFrame();
      if (signal.aborted) return;
      for (let index = 0; index < labels.length && version === this.syncVersion; index++) {
        const dropdown = this.widget.querySelectorAll('.kaching-bundles__bar--selected .kaching-bundles__swatch-dropdown')[index];
        if (!dropdown) return;
        if (dropdown.dataset.value === labels[index]) continue;
        dropdown.querySelector('[role="combobox"]').click();
        await this.nextFrame();
        if (signal.aborted) return;
        const option = [...dropdown.querySelectorAll('[role="option"]')].find((option) => option.dataset.value === labels[index]);
        if (!option) return;
        option.click();
        await this.nextFrame();
        if (signal.aborted) return;
      }
      if (version !== this.syncVersion) continue;
      if (this.sock && this.gifts(tier).some((gift) => gift.key === 'socks')) {
        const products = JSON.parse(this.widget.getAttribute('other-products'));
        const sock = products.flatMap((product) => product.variants).find((variant) => Number(variant.id) === this.sock);
        const select = [...this.widget.querySelectorAll('.kaching-bundles__bar--selected .kaching-bundles__free-gift select')]
          .find((select) => [...select.options].some((option) => option.value === sock?.options[0]));
        if (!select) return;
        select.value = sock.options[0];
        select.dispatchEvent(new Event('change', { bubbles: true }));
        await this.nextFrame();
        if (signal.aborted) return;
      }
      this.synced = this.matchesWidget();
    } while (version !== this.syncVersion && !this.bridgeFailed && this.isConnected);
  }

  matchesWidget() {
    try {
      if (!this.widget || this.widget.selectedDealBarId !== this.bar()?.id || this.recent.length !== this.quantity) return false;
      const expected = new Map();
      for (const id of this.recent) expected.set(id, (expected.get(id) || 0) + 1);
      for (const gift of this.gifts(this.bar())) {
        const id = gift.key === 'socks' ? this.sock : gift.variants[0]?.id;
        if (!id) return false;
        expected.set(id, 1);
      }
      const actual = new Map();
      for (const item of this.widget.items()) actual.set(Number(item.id), (actual.get(Number(item.id)) || 0) + Number(item.quantity));
      return expected.size === actual.size && [...expected].every(([id, quantity]) => actual.get(id) === quantity);
    } catch { return false; }
  }

  correctCartCount(event) {
    const data = event.detail?.data;
    if (!this.inFlight || event.target !== this.form || data?.source !== 'product-form-component'
      || String(data.productId) !== this.dataset.productId || data.isIncremental !== true) return;
    const counts = Object.values(data.sections || {}).flatMap((html) => {
      if (typeof html !== 'string') return [];
      const document = new DOMParser().parseFromString(html, 'text/html');
      return [...document.querySelectorAll('[ref="cartItemCount"]')].map((element) => element.textContent.trim())
        .filter((value) => /^\d+$/.test(value)).map(Number).filter(Number.isSafeInteger);
    });
    if (!counts.length || new Set(counts).size !== 1) { this.countUnresolved = true; return; }
    // Amend only the native event's count; its response, sections and target stay intact.
    data.itemCount = counts[0];
    data.isIncremental = false;
    this.countUnresolved = false;
  }

  releasePress() {
    this.finishStickyPress();
    this.inFlight = false;
    this.allowSubmit = false;
    clearTimeout(this.pressTimer);
    this.pressToken = null;
    this.stopWatching?.();
  }

  watchNativeRequest() {
    // Observe one native promise; captured wrappers remain inert after it settles.
    const previous = window.fetch;
    const token = this.pressToken = Symbol('chooser press');
    let active = true;
    let observed = false;
    const uninstall = () => {
      active = false;
      if (window.fetch === watched) window.fetch = previous;
    };
    const failed = (error) => {
      console.warn('Chooser native add failed', error);
      if (this.pressToken !== token) return;
      this.releasePress();
      this.form?.refs.addToCartButtonContainer?.removeLoading();
    };
    const watched = (...args) => {
      let observe = false;
      if (active && !observed && this.inFlight && this.pressToken === token) {
        try {
          const url = new URL(args[0]?.url || args[0], location.href);
          observe = url.pathname === new URL(window.FoxTheme.routes.cart_add_url, location.href).pathname;
        } catch { /* Invalid URLs remain the native fetch's responsibility. */ }
      }
      if (observe) observed = true;
      let result;
      try { result = previous.apply(window, args); }
      catch (error) { if (observe) { uninstall(); failed(error); } throw error; }
      if (observe) result.then((response) => {
        uninstall();
        if (!this.inFlight || this.pressToken !== token) return;
        try { response.clone().json().catch(failed); } catch (error) { failed(error); }
      }, (error) => { uninstall(); failed(error); });
      return result;
    };
    this.stopWatching = uninstall;
    window.fetch = watched;
    this.pressTimer = setTimeout(() => {
      if (this.pressToken !== token) return;
      this.releasePress();
      this.form?.refs.addToCartButtonContainer?.removeLoading();
    }, 20000);
  }

  stopPress(event) { event.preventDefault(); event.stopImmediatePropagation(); }

  // G2: a waiting press ends with nothing added; both buttons lose the loading state; the chooser stays.
  endWait() {
    if (!this.waiting) return;
    this.waitToken = null;
    this.waiting = false;
    this.form?.refs.addToCartButtonContainer?.removeLoading();
    this.finishStickyPress();
  }

  async guardPress(event) {
    if (!this.form?.isConnected) { this.restoreWidget(); return; }
    if (this.bridgeFailed || !this.hasAttribute('data-kaching-active')) return;
    if (this.inFlight || this.waiting) { this.stopPress(event); return; }
    // F4: no box chosen yet: stop, and lead her to the box options; Kaching's widget stays hidden.
    if (!this.quantity) {
      this.stopPress(event);
      this.form?.refs.addToCartButtonContainer?.removeLoading();
      this.scrollToBoxes();
      return;
    }
    if (!this.choiceComplete()) {
      // An incomplete choice stops here; restoreWidget stays for real bridge failures.
      this.stopPress(event);
      this.flavourError = this.recent.length !== this.quantity;
      this.sockError = this.gifts(this.bar()).some((gift) => gift.key === 'socks') && !this.sock;
      this.blurChoice();
      this.render();
      this.form?.refs.addToCartButtonContainer?.removeLoading();
      const add = this.ui.querySelector('[data-variant][data-delta="1"]:not(:disabled)');
      // The sock group sits above the flavours, so it takes focus first.
      if (this.sockError) this.leadTo('sock');
      else if (add) this.leadTo('flavour');
      // No flavour can take a box: Kaching's widget comes back, as before.
      else this.restoreWidget();
      return;
    }
    if (this.synced && this.matchesWidget()) {
      this.inFlight = true;
      this.watchNativeRequest();
      this.allowSubmit = event.type === 'click';
      return;
    }
    this.stopPress(event);
    const signal = this.abort.signal;
    const token = this.waitToken = Symbol('chooser wait');
    const pressed = performance.now();
    this.waiting = true;
    this.form?.refs.addToCartButtonContainer?.addLoading();
    this.queueSync();
    // G2: no time limit while Kaching's widget is absent (the cap ends that); 3 s once the widget is there.
    while (this.waitToken === token && !signal.aborted && this.isConnected && !this.bridgeFailed && !(this.synced && this.matchesWidget())) {
      if (!this.widget) this.widgetReady?.();
      else {
        if (performance.now() >= Math.max(pressed, this.widgetTakenAt) + 3000) break;
        if (!this.syncing) this.queueSync();
      }
      await this.nextFrame();
    }
    if (this.waitToken !== token || signal.aborted || !this.isConnected) return;
    this.waitToken = null;
    this.waiting = false;
    // restoreWidget has already run (the cap, or the form left the page).
    if (this.bridgeFailed) return;
    if (!this.synced || !this.matchesWidget()) { this.restoreWidget(); return; }
    this.form?.refs.addToCartButtonContainer?.removeLoading();
    const button = this.form?.querySelector('button[type="submit"]');
    if (!button) { this.restoreWidget(); return; }
    button.click();
  }

  escape(value) {
    return String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  }

  word(key, values = {}) { return this.fill(this.config.words[key], values); }

  fill(text, values = {}) {
    let value = this.escape(text || '');
    for (const [name, replacement] of Object.entries(values)) {
      const text = this.escape(replacement);
      value = value.replaceAll(`[${name}]`, ['value', 'n', 'percent', 'size'].includes(name) ? `<bdi>${text}</bdi>` : text);
    }
    return value;
  }

  // G1: a text role set in the editor; its classes and style go on the text element only.
  // E6: its colour and alignment, when set, ride on CSS variables the stylesheet reads; align false keeps an element's own alignment.
  // E12: color false keeps an element's own colour (struck prices keep the secondary text colour).
  look(role, base = '', align = true, color = true) {
    const look = { ...this.config.look?.[role] };
    if (!align) look.align = '';
    if (!color) look.color = '';
    const classes = [base, look.class].filter(Boolean).join(' ');
    const style = `${look.class ? look.style : ''}${look.color ? `--chooser-color: ${look.color};` : ''}${look.align ? `--chooser-align: ${look.align}; --chooser-align-mobile: ${look.align_mobile};` : ''}`;
    return [classes && `class="${classes}"`, look.class && 'data-look', look.color && 'data-color', look.align && 'data-align', style && `style="${this.escape(style)}"`].filter(Boolean).join(' ');
  }

  money(value) { return formatCurrency(Math.round(value), window.FoxTheme?.moneyFormat); }
  price(value) { return `<bdi>${this.escape(this.money(value))}</bdi>`; }
  giftMoney(value) {
    // Select a currency mask for whole gifts, preserving the theme's currency and ordering.
    const mask = window.FoxTheme?.moneyFormat;
    return formatCurrency(Math.round(value), value % 100 === 0 && mask
      ? mask.replace(/\{\{\s*\w+\s*\}\}0?/, '{{amount_no_decimals}}') : mask);
  }
  giftPrice(value) { return `<bdi>${this.escape(this.giftMoney(value))}</bdi>`; }
  availableVariants() { return this.config.variants.filter((variant) => variant.available); }
  // Cards follow the Box option blocks' order; two blocks with one quantity: the first wins.
  tier(quantity) { return this.tiers.find((tier) => tier.quantity === quantity); }
  pathBars(path = this.path) { return this.bars.filter((bar) => (bar.freeGifts.length > 0) === (path === 'new')).sort((a, b) => this.tiers.indexOf(this.tier(a.quantity)) - this.tiers.indexOf(this.tier(b.quantity))); }
  bar(quantity = this.quantity) { return this.pathBars().find((bar) => bar.quantity === quantity); }
  gifts(bar) { return this.config.gifts.filter((gift) => gift.id && bar?.freeGifts.some((entry) => entry.productGID === `gid://shopify/Product/${gift.id}`)); }
  percent(bar) { return bar?.discountType === 'percentage' ? Number(bar.discountValue) : 0; }

  // F2: her picks stay and new slots stay empty; a smaller box count drops the oldest pick.
  // F4: a box count missing on this path becomes none (0), never the first option.
  reconcile(quantity) {
    if (!this.bar(quantity)) quantity = 0;
    const variants = this.availableVariants();
    this.recent = this.recent.filter((id) => variants.some((variant) => variant.id === id));
    // With no box chosen her picks are kept (a path switch keeps them); the next box count trims them.
    while (quantity && this.recent.length > quantity) this.recent.shift();
    this.quantity = quantity;
    if (!this.gifts(this.bar()).some((gift) => gift.key === 'socks')) this.sock = null;
  }

  // F2: the back button only. One saved choice per product, for this tab.
  stateKey() { return `lumeia-chooser:${this.dataset.productId}:${this.dataset.blockId}`; }

  readState() {
    try { const state = JSON.parse(sessionStorage.getItem(this.stateKey())); return state && typeof state === 'object' ? state : null; } catch { return null; }
  }

  saveState() {
    try { sessionStorage.setItem(this.stateKey(), JSON.stringify({ path: this.path, quantity: this.quantity, recent: this.recent, sock: this.sock })); } catch { /* ignored */ }
  }

  applyState(state) {
    this.path = ['new', 'returning'].includes(state.path) && this.pathBars(state.path).length ? state.path : 'new';
    if (!this.pathBars().length && this.pathBars('returning').length) this.path = 'returning';
    const quantity = Number(state.quantity);
    this.quantity = this.bar(quantity) ? quantity : 0;
    const variants = this.availableVariants();
    this.recent = (Array.isArray(state.recent) ? state.recent : []).map(Number).filter((id) => variants.some((variant) => variant.id === id));
    const socks = this.gifts(this.bar()).find((gift) => gift.key === 'socks');
    this.sock = socks?.variants.some((variant) => variant.id === Number(state.sock)) ? Number(state.sock) : null;
    this.picked = true;
    this.clearErrors();
  }

  freshStart() {
    this.path = 'new';
    if (!this.pathBars().length && this.pathBars('returning').length) this.path = 'returning';
    this.quantity = this.startQuantity;
    this.recent = [];
    this.sock = null;
    this.picked = false;
    this.clearErrors();
    try { sessionStorage.removeItem(this.stateKey()); } catch { /* ignored */ }
  }

  clearErrors() { this.flavourError = false; this.sockError = false; this.toast = false; }

  change(event) {
    const input = event.target;
    this.picked = true;
    this.toast = false;
    this.flavourError = false;
    if (input.hasAttribute('data-path')) { this.sockError = false; this.path = input.value; this.reconcile(this.quantity); this.switchRender(); return; }
    else if (input.hasAttribute('data-tier')) { this.sockError = false; this.reconcile(Number(input.value)); this.boxRender(); return; }
    else if (input.hasAttribute('data-sock')) { this.sock = Number(input.value); this.sockError = false; }
    else return;
    this.render();
  }

  // Reduced motion, or the theme's animations off: every change is instant.
  moves() { return this.config.motion && !matchMedia('(prefers-reduced-motion: reduce)').matches && Boolean(this.ui.animate); }

  fadeIn(element) { element?.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 220, easing: 'ease-out' }); }

  // F3 and F6: the height moves from the old to the new value (220 ms ease-out), the lower edge clipped only,
  // so a badge above the element stays whole. Inline styles are removed after.
  glide(element, from, to, fade = false) {
    element.style.clipPath = 'inset(-16px -16px 0 -16px)';
    // A gift row can move twice at once; its clip stays until the last motion ends.
    const done = () => { if (!element.getAnimations().length) element.style.removeProperty('clip-path'); };
    element.animate([{ height: `${from}px`, ...(fade ? { opacity: 0 } : {}) }, { height: `${to}px`, ...(fade ? { opacity: 1 } : {}) }], { duration: 220, easing: 'ease-out' }).finished.then(done, done);
  }

  // F3: a route switch fades the new box options in while their height moves from the old to the new.
  // A second switch starts from the height shown at that moment.
  switchRender() {
    const area = this.ui.querySelector('.lumeia-bundle-chooser__tiers');
    const from = area?.getBoundingClientRect().height;
    this.render();
    const next = this.ui.querySelector('.lumeia-bundle-chooser__tiers');
    if (!this.moves() || !area || !next) return;
    this.glide(next, from, next.getBoundingClientRect().height, true);
  }

  // F6: a new box count moves each box card from its old height to its new one, so the cards below slide;
  // the chosen card's ticks and sock group fade in. A second change starts from the heights on screen.
  boxRender() {
    const before = new Map([...this.ui.querySelectorAll('[data-deal-bar-id]')].map((card) => [card.dataset.dealBarId, card.getBoundingClientRect().height]));
    this.render();
    if (!this.moves()) return;
    for (const card of this.ui.querySelectorAll('[data-deal-bar-id]')) {
      const from = before.get(card.dataset.dealBarId);
      const to = card.getBoundingClientRect().height;
      if (from === undefined || Math.abs(from - to) < 0.5) continue;
      this.glide(card, from, to);
      if (card.classList.contains('is-selected')) for (const part of card.querySelectorAll(':scope > .lumeia-bundle-chooser__points, :scope > .lumeia-bundle-chooser__socks')) this.fadeIn(part);
    }
  }

  // F6: the browser opens and closes the gift row itself; on the next frame its height moves to the new value,
  // and opening fades the content in. No render, so her focus stays on the summary.
  glideGifts(details) {
    if (!this.moves()) return;
    // A card still moving would clip the opened row; it settles at once instead.
    details.closest('[data-deal-bar-id]')?.getAnimations().forEach((animation) => animation.cancel());
    const from = details.getBoundingClientRect().height;
    // F14: the header's thumbnail row collapses or opens and fades, from what is on screen now.
    const summary = details.querySelector(':scope > summary');
    const thumbs = summary.querySelector('.lumeia-bundle-chooser__gift-thumbs');
    const rows = getComputedStyle(summary).gridTemplateRows;
    const shown = getComputedStyle(thumbs).opacity;
    // Held at the old height until the next frame, so the browser's own toggle never shows for one frame.
    details.style.height = `${from}px`;
    details.style.clipPath = 'inset(-16px -16px 0 -16px)';
    summary.style.gridTemplateRows = rows;
    Object.assign(thumbs.style, { opacity: shown, visibility: 'visible' });
    requestAnimationFrame(() => {
      details.style.removeProperty('height');
      summary.style.removeProperty('grid-template-rows');
      for (const name of ['opacity', 'visibility']) thumbs.style.removeProperty(name);
      // A second press during the motion: the new motion starts from the height read above.
      for (const element of [details, summary, thumbs]) element.getAnimations().forEach((animation) => animation.cancel());
      // Read before the header moves, so the row ends at its real height.
      const to = details.getBoundingClientRect().height;
      const rowsTo = getComputedStyle(summary).gridTemplateRows;
      // Only the stacked header (two rows) moves and fades its thumbnail row; side by side it swaps at once.
      if (rowsTo !== rows && rows.split(' ').length === 2 && rowsTo.split(' ').length === 2) {
        summary.animate([{ gridTemplateRows: rows }, { gridTemplateRows: rowsTo }], { duration: 220, easing: 'ease-out' });
        thumbs.animate([{ opacity: shown, visibility: 'visible' }, { opacity: details.open ? 0 : 1, visibility: 'visible' }], { duration: 220, easing: 'ease-out' });
      }
      if (Math.abs(from - to) < 0.5) { if (!details.getAnimations().length) details.style.removeProperty('clip-path'); return; }
      this.glide(details, from, to);
      if (details.open) this.fadeIn(details.querySelector('.lumeia-bundle-chooser__gift-content'));
    });
  }

  mixedAvailable() { return this.config.variants.length === 3 && this.config.variants.every((variant) => variant.available) && Boolean(this.bar(3)); }

  click(event) {
    const summary = event.target.closest('.lumeia-bundle-chooser__gift-details > summary');
    if (summary) { this.glideGifts(summary.parentElement); return; }
    const card = event.target.closest('[data-deal-bar-id]');
    if (card && !card.contains(event.target.closest('label, input, details, fieldset, button'))) { this.picked = true; this.sockError = false; this.flavourError = false; this.reconcile(Number(card.querySelector('[data-tier]').value)); this.boxRender(); return; }
    const button = event.target.closest('button');
    if (!button || button.disabled) return;
    this.toast = false;
    this.flavourError = false;
    if (button.hasAttribute('data-mixed')) {
      if (!this.mixedAvailable()) return;
      if (this.quantity !== 3) this.sockError = false;
      this.reconcile(3);
      this.recent = this.config.variants.map((variant) => variant.id);
      this.toast = true;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.toast = false;
        const toast = this.ui.querySelector('[data-toast]');
        if (toast) { toast.hidden = true; toast.textContent = ''; }
      }, 2800);
    } else if (button.dataset.variant) {
      const id = Number(button.dataset.variant);
      if (!this.availableVariants().some((variant) => variant.id === id)) return;
      if (Number(button.dataset.delta) < 0) {
        const index = this.recent.lastIndexOf(id);
        if (index >= 0) this.recent.splice(index, 1);
      } else if (this.recent.length < this.quantity) this.recent.push(id);
      else {
        const index = this.recent.findIndex((selected) => selected !== id);
        if (index >= 0) { this.recent.splice(index, 1); this.recent.push(id); }
      }
    } else return;
    this.picked = true;
    if (button.hasAttribute('data-mixed')) this.boxRender();
    else this.render();
  }

  // F13: the sign is drawn, so it sits in the exact centre of its circle; the button keeps its label.
  sign(plus) { return `<svg class="lumeia-bundle-chooser__sign" aria-hidden="true" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"><path vector-effect="non-scaling-stroke" d="M1 6h10${plus ? 'M6 1v10' : ''}"/></svg>`; }

  icon(name) {
    const paths = {
      check: '<path d="M2 8l4 4 8-8" stroke="currentColor" stroke-width="2"/>',
      pending: '<circle cx="8" cy="8" r="6.25" stroke="currentColor" stroke-width="1.5"/>',
      alert: '<circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/><path d="M8 4.5V9M8 11.5v.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>'
    };
    return `<svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">${paths[name]}</svg>`;
  }

  // Theme editor only: say which Box option blocks or Kaching options are hidden, and why.
  editorNotice() {
    if (this.dataset.designMode !== 'true' || !this.allBars?.length) return '';
    const deal = [...new Set(this.allBars.map((bar) => bar.quantity))];
    const lines = [
      ...[...new Set(this.tiers.map((tier) => tier.quantity))].filter((quantity) => !deal.includes(quantity)).map((quantity) => `Box option for ${quantity} boxes: no matching option in the Kaching deal, so it is hidden.`),
      ...deal.filter((quantity) => !this.tier(quantity)).map((quantity) => `Kaching option for ${quantity} boxes: no Box option block, so it is hidden.`),
      ...[...new Set(this.tiers.map((tier) => tier.quantity).filter((quantity, index, all) => all.indexOf(quantity) !== index))]
        .map((quantity) => `Two Box option blocks use ${quantity} boxes; the first one is used.`)
    ];
    return lines.length ? `<ul class="lumeia-bundle-chooser__editor-notice" role="note">${lines.map((line) => `<li>${this.escape(line)}</li>`).join('')}</ul>` : '';
  }

  // F8: a key lets render() keep the same picture node across renders.
  giftImage(gift, key = '') { return gift.image ? `<img src="${this.escape(gift.image)}" alt="" width="80" height="80" loading="lazy"${key ? ` data-img="${this.escape(key)}"` : ''}>` : ''; }

  giftRow(tier, opened) {
    const gifts = this.gifts(tier);
    if (!gifts.length) return '';
    const value = gifts.reduce((sum, gift) => sum + gift.price, 0);
    return `<details class="lumeia-bundle-chooser__gift-details" data-gift-tier="${this.escape(tier.id)}" ${opened.has(tier.id) ? 'open' : ''}>
      <summary><span class="lumeia-bundle-chooser__gift-thumbs" style="--gift-count: ${gifts.length}">
        ${gifts.map((gift) => `<span class="lumeia-bundle-chooser__gift-thumb">${this.giftImage(gift, `thumb-${tier.id}-${gift.key}`)}<s ${this.look('gift_row', '', false, false)}>${this.giftPrice(gift.price)}</s></span>`).join('')}
      </span><span ${this.look('gift_row', 'lumeia-bundle-chooser__gift-caption', false)}><span class="lumeia-bundle-chooser__gift-line">${this.word(tier.quantity === 1 ? 'gift_summary_1' : 'gift_summary_kit')}</span>
        <span>${this.word(gifts.length === 1 ? 'gift_value_one' : 'gift_value_many', { value: this.giftMoney(value), n: gifts.length })}</span></span><svg class="lumeia-bundle-chooser__chevron" aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 6l5 5 5-5" stroke="currentColor" stroke-width="2"/></svg></summary>
      <div class="lumeia-bundle-chooser__gift-content"><p ${this.look('gift_row', '', false)}>${this.word('gift_details')}</p><div class="lumeia-bundle-chooser__gifts">${gifts.map((gift) => `<div class="lumeia-bundle-chooser__gift">
        ${this.giftImage(gift, `gift-${tier.id}-${gift.key}`)}<s ${this.look('gift_row', '', false, false)}>${this.giftPrice(gift.price)}</s><span ${this.look('gift_row')}>${this.word(`gift_${gift.key}`)}</span></div>`).join('')}</div></div>
    </details>`;
  }

  points(tier) {
    if (tier.quantity !== this.quantity) return '';
    const value = this.gifts(tier).reduce((sum, gift) => sum + gift.price, 0);
    const lines = (this.tier(tier.quantity)?.points || []).map((point) => {
      if (!point || (point.includes('[percent]') && !this.percent(tier))) return '';
      if (point === '[gifts]') return this.path === 'new' ? this.word('point_gifts', { value: this.giftMoney(value) }) : '';
      return this.fill(point, { percent: this.percent(tier) });
    }).filter(Boolean);
    return lines.length ? `<ul class="lumeia-bundle-chooser__points" id="chooser-points-${this.dataset.blockId}-${tier.quantity}">${lines.map((line) => `<li>${this.icon('check')}<span ${this.look('tick')}>${line}</span></li>`).join('')}</ul>` : '';
  }

  sockGroup(tier) {
    if (tier.quantity !== this.quantity) return '';
    const socks = this.gifts(tier).find((gift) => gift.key === 'socks');
    if (!socks) return '';
    const uid = this.dataset.blockId;
    const errorId = `chooser-sock-error-${uid}`;
    return `<fieldset class="lumeia-bundle-chooser__socks" ${this.sockError && !this.sock ? `aria-describedby="${errorId}"` : ''}><legend ${this.look('socks_heading')}>${this.word('sock_heading')} <span ${this.look('socks_tag', 'lumeia-bundle-chooser__required')}>${this.word('sock_required')}</span></legend>
      ${socks.variants.map((variant) => `<label class="lumeia-bundle-chooser__sock"><input type="radio" required name="chooser-sock-${uid}" value="${variant.id}" data-sock data-focus="sock-${variant.id}" ${this.sock === variant.id ? 'checked' : ''}><span ${this.look('sock_row')}>${this.escape(variant.name)}</span></label>`).join('')}
      <p id="${errorId}" role="alert" data-sock-error ${this.sockError && !this.sock ? '' : 'hidden'}>${this.word('sock_error')}</p></fieldset>`;
  }

  flavourImage(variant, key) {
    const source = [...this.querySelectorAll('[data-flavour-option]')].find((item) => Number(item.dataset.flavourVariant) === variant.id || item.dataset.flavourOption === variant.option);
    const image = source?.querySelector('img')?.cloneNode(true);
    if (image) { image.loading = 'lazy'; image.alt = ''; image.dataset.img = key; return image.outerHTML; }
    return variant.image ? `<img src="${this.escape(variant.image)}" alt="" width="150" height="150" loading="lazy" data-img="${this.escape(key)}">` : '';
  }

  // The Liquid-rendered picture, phone source included, so the browser picks the file (Returning, New since F5, Mixed since F9).
  routeMedia(path) {
    const media = this.querySelector(`[data-${path}-media] .lumeia-bundle-chooser__${path}-media`)?.cloneNode(true);
    if (!media) return '';
    for (const image of media.querySelectorAll('img')) { image.loading = 'lazy'; image.alt = ''; image.dataset.img = `${path}-photo`; }
    return media.outerHTML;
  }

  // G4 with a box option title set in the editor: the radio follows that title's line height (also on resize).
  alignRadio() {
    const title = this.config.look?.box_title?.class && this.ui.querySelector('.lumeia-bundle-chooser__title');
    if (!title) return;
    const style = getComputedStyle(title);
    const line = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2;
    this.style.setProperty('--chooser-radio-offset', `${Math.max(0, (line - parseFloat(getComputedStyle(this).getPropertyValue('--chooser-radio-size'))) / 2)}px`);
  }

  render() {
    if (this.bridgeFailed) return;
    const active = document.activeElement;
    const focusKey = active && this.ui.contains(active) ? active.dataset.focus : null;
    const opened = new Set([...this.ui.querySelectorAll('details[open]')].map((details) => details.dataset.giftTier));
    const uid = this.dataset.blockId;
    const bar = this.bar();
    const gifts = this.gifts(bar);
    const socks = gifts.find((gift) => gift.key === 'socks');
    const maxValue = Math.max(0, ...this.bars.map((tier) => this.gifts(tier).reduce((sum, gift) => sum + gift.price, 0)));
    const pathCards = ['new', 'returning'].map((path) => `<label class="lumeia-bundle-chooser__card lumeia-bundle-chooser__path">
      <input type="radio" name="chooser-path-${uid}" value="${path}" data-path data-focus="path-${path}">
      <span class="lumeia-bundle-chooser__path-media"><span ${this.look('route_pill', 'lumeia-bundle-chooser__path-pill')}>${this.word(`${path}_title`)}</span>
        ${path === 'new' ? this.routeMedia('new') || this.config.gifts.map((gift) => this.giftImage(gift, `new-${gift.key}`)).join('') : this.routeMedia('returning')}
      </span><strong ${this.look('route_title', 'lumeia-bundle-chooser__path-title')}>${this.word(`${path}_subtitle`)}</strong>
      <span ${this.look('route_line')}>${path === 'new' ? this.word('new_value', { value: this.giftMoney(maxValue) }) : this.word('returning_description')}</span>
    </label>`).join('');
    const tiers = this.pathBars().map((tier) => {
      const quantity = tier.quantity;
      const words = this.tier(quantity);
      const percent = this.percent(tier);
      const full = (this.availableVariants()[0]?.price || this.config.variants[0]?.price || 0) * quantity;
      return `<article class="lumeia-bundle-chooser__card lumeia-bundle-chooser__tier ${quantity === this.quantity ? 'is-selected' : ''}" data-deal-bar-id="${this.escape(tier.id)}">
        <label class="lumeia-bundle-chooser__tier-selector">${words.badge ? `<span ${this.look('badge', 'lumeia-bundle-chooser__badge')}>${this.fill(words.badge)}</span>` : ''}<span class="lumeia-bundle-chooser__tier-main"><input type="radio" name="chooser-tier-${uid}" value="${quantity}" ${this.points(tier) ? `aria-describedby="chooser-points-${uid}-${quantity}"` : ''} data-tier data-focus="tier-${quantity}" ${quantity === this.quantity ? 'checked' : ''}>
          <span class="lumeia-bundle-chooser__tier-copy"><strong ${this.look('box_title', 'lumeia-bundle-chooser__title')}>${this.fill(words.title)}</strong>
            ${percent ? `<span ${this.look('saving_pill', 'lumeia-bundle-chooser__saving')}>${this.word('saving', { percent })}</span>` : ''}<span ${this.look('box_subtitle', 'lumeia-bundle-chooser__subtitle')}>${this.fill(words.subtitle)}</span></span></span>
          <span class="lumeia-bundle-chooser__tier-price"><span ${this.look('price', 'lumeia-bundle-chooser__price')}>${this.price(full * (1 - percent / 100))}</span>
            ${percent ? `<s ${this.look('price', '', false, false)}>${this.price(full)}</s>` : ''}</span></label>${this.points(tier)}
        ${this.giftRow(tier, opened)}${this.sockGroup(tier)}
      </article>`;
    }).join('');
    const flavours = this.config.variants.map((variant) => {
      const count = this.recent.filter((id) => id === variant.id).length;
      return `<div class="lumeia-bundle-chooser__card lumeia-bundle-chooser__flavour ${count ? 'is-selected' : ''} ${!variant.available ? 'is-unavailable' : ''}" aria-disabled="${!variant.available}">
        ${this.flavourImage(variant, `flavour-${variant.id}`)}<span ${this.look('flavour_name')}>${this.escape(variant.name)}</span><div class="lumeia-bundle-chooser__controls">
          <button type="button" data-variant="${variant.id}" data-delta="-1" data-focus="minus-${variant.id}" aria-label="${this.word('remove_flavour', { flavour: variant.name })}" ${!variant.available || count === 0 ? 'disabled' : ''}>${this.sign(false)}</button>
          <span ${this.look('flavour_count')}><bdi>${count}</bdi></span><button type="button" data-variant="${variant.id}" data-delta="1" data-focus="plus-${variant.id}" aria-label="${this.word('add_flavour', { flavour: variant.name })}" ${!variant.available || !bar ? 'disabled' : ''}>${this.sign(true)}</button>
        </div></div>`;
    }).join('');
    const selected = this.recent.length;
    const remaining = this.quantity - selected;
    const mixed = this.quantity === 3 && this.config.variants.every((variant) => this.recent.filter((id) => id === variant.id).length === 1);
    // The selected tier card already names the tier, the saving and the gifts; only the box progress and the sock choice are added here.
    const needed = remaining ? this.word(remaining === 1 ? 'remaining_one' : 'remaining_many', { n: remaining }) : '';
    const countText = this.word(selected === 1 ? 'count_one' : 'count_many', { n: selected });
    // One segment per box; the segment just filled animates once.
    const grew = selected > (this.shownSelected ?? selected);
    this.shownSelected = selected;
    const segments = Array.from({ length: this.quantity }, (_, index) => `<span ${index < selected ? 'data-filled' : ''} ${grew && index === selected - 1 ? 'data-new' : ''}></span>`).join('');
    const sockPending = Boolean(socks) && !this.sock;
    let sockLine = '';
    // While boxes are short, the sock prompt sits under the box line and shares its icon.
    if (sockPending && remaining) sockLine = `<p class="lumeia-bundle-chooser__line lumeia-bundle-chooser__subline" ${this.sockError ? 'data-error' : ''}><span ${this.look('summary')}>${this.word('summary_sock_pending')}</span></p>`;
    else if (sockPending) sockLine = `<p class="lumeia-bundle-chooser__line" ${this.sockError ? 'data-error' : ''}>${this.icon('alert')}<span ${this.look('summary')}>${this.word('summary_sock_pending')}</span></p>`;
    else if (socks) sockLine = `<p class="lumeia-bundle-chooser__line">${this.icon('check')}<span ${this.look('summary')}>${this.word('summary_sock', { size: socks.variants.find((variant) => variant.id === this.sock)?.name || '' })}</span></p>`;
    // The flavour error's line is hidden from screen readers, so an alert reads today's words.
    const flavourAlert = this.flavourError && remaining > 0;
    const flavourErrorId = `chooser-flavour-error-${this.dataset.blockId}`;
    // The bar sits below every line, so it stays put when a sock size is picked.
    const progress = `<div class="lumeia-bundle-chooser__progress" ${this.flavourError && remaining ? 'data-error' : ''}><p class="lumeia-bundle-chooser__line" aria-hidden="true">${this.icon(remaining ? (this.flavourError ? 'alert' : 'pending') : 'check')}<span ${this.look('summary')}>${needed || countText}</span></p>${sockLine}
          <div class="lumeia-bundle-chooser__bar" role="progressbar" aria-label="${this.word('step_flavour')}" aria-valuemin="0" aria-valuemax="${this.quantity}" aria-valuenow="${selected}">${segments}</div></div>`;
    // F3: the route cards are written once and kept, pictures included; each render sets only their checked and disabled states.
    const routes = `<fieldset><legend ${this.look('step')}>${this.word('step_path')}</legend><div class="lumeia-bundle-chooser__paths">${pathCards}</div></fieldset>`;
    const rest = `<fieldset><legend ${this.look('step')}>${this.word('step_tier')}</legend>${this.editorNotice()}<div class="lumeia-bundle-chooser__tiers">${tiers}</div></fieldset>
      <fieldset ${flavourAlert ? `aria-describedby="${flavourErrorId}"` : ''}><legend ${this.look('step')}>${this.word('step_flavour')}</legend><div class="lumeia-bundle-chooser__flavours">${flavours}</div>
        ${this.bar(3) ? `<p ${this.look('or_choose', 'lumeia-bundle-chooser__or')}>${this.word('or_choose')}</p><button type="button" class="lumeia-bundle-chooser__card lumeia-bundle-chooser__mixed ${mixed ? 'is-selected' : ''}" data-mixed data-focus="mixed" aria-pressed="${mixed}" ${this.mixedAvailable() ? '' : 'disabled'}>
          <span class="lumeia-bundle-chooser__mixed-images">${this.routeMedia('mixed') || this.config.variants.map((variant, slot) => this.flavourImage(variant, `mixed-${slot}`)).join('')}</span>
          <span><strong ${this.look('route_title', 'lumeia-bundle-chooser__path-title')}>${this.word('mixed_title')}</strong><span ${this.look('mixed_text')}>${this.word('mixed_description')}</span></span></button>` : ''}
        <p class="visually-hidden" role="status">${this.quantity ? `<span>${countText}</span>${needed ? ` <span>${needed}</span>` : ''}` : ''}</p>
        ${flavourAlert ? `<p id="${flavourErrorId}" class="visually-hidden" role="alert">${needed}</p>` : ''}
        ${bar ? `<div class="lumeia-bundle-chooser__summary">${progress}</div>` : ''}
        <p data-toast role="status" aria-live="polite" ${this.toast ? '' : 'hidden'}>${this.toast ? this.word('mixed_toast') : ''}</p>
      </fieldset>`;
    // F8: the pictures already shown are moved into the new markup by key, so a press re-decodes and fades none of them.
    const shown = new Map([...this.ui.querySelectorAll('img[data-img]')].map((image) => [image.dataset.img, image]));
    const route = this.ui.firstElementChild;
    if (route && this.routeHtml === routes) {
      while (route.nextSibling) route.nextSibling.remove();
      route.insertAdjacentHTML('afterend', rest);
    } else {
      this.ui.innerHTML = routes + rest;
      this.routeHtml = routes;
    }
    for (const image of this.ui.querySelectorAll('img[data-img]')) {
      const old = shown.get(image.dataset.img);
      if (old && old !== image && old.getAttribute('src') === image.getAttribute('src')) image.replaceWith(old);
    }
    for (const input of this.ui.querySelectorAll('[data-path]')) {
      input.checked = input.value === this.path;
      input.disabled = !this.pathBars(input.value).length;
    }
    if (this.bars.length) this.setAttribute('data-kaching-active', '');
    this.alignRadio();
    this.queueSync();
    this.updateSticky();
    if (this.picked) this.saveState();
    if (focusKey) [...this.ui.querySelectorAll('[data-focus]')].find((item) => item.dataset.focus === focusKey)?.focus({ preventScroll: true });
  }
}

if (!customElements.get('lumeia-bundle-chooser')) customElements.define('lumeia-bundle-chooser', LumeiaBundleChooser);
